import React, { useState, useEffect, useRef } from "react";
import { useAccount, useSignMessage } from "wagmi";
import { ConnectButton } from "@rainbow-me/rainbowkit";
import { ethers, BigNumber } from "ethers";
import { abi } from "../components/ABI";
import { getNFTCollection } from "../api";
import {
  NftAccountRow,
  NftChipBtn,
  NftHint,
  NftMeta,
  NftMetaLabel,
  NftMetaValue,
  NftPanel,
  NftPrimaryBtn,
  NftRoot,
  NftStatusError,
  NftStatusOk,
  NftVerified,
  NftWrongNetworkBtn,
} from "./NFTAuth.styles";

const WalletIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4" />
    <path d="M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4" />
    <circle cx="17" cy="12" r="1" fill="currentColor" stroke="none" />
  </svg>
);

const CheckIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const AlertIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M12 8v4" />
    <path d="M12 16h.01" />
  </svg>
);

const NFTAuth = (props) => {
  const { address, isConnected } = useAccount();
  const [nftCount, setNftCount] = useState(0);
  const [isError, setIsError] = useState(false);
  const [signature, setSignature] = useState(null);
  const [contractAddresses, setContractAddresses] = useState([]);
  const [balances, setBalances] = useState([]);

  useEffect(() => {
    async function fetchContracts() {
      try {
        const res = await getNFTCollection();
        const contracts = res?.data?.map((item) => item.contractAddress) || [];
        setContractAddresses(contracts);
      } catch (e) {
        setContractAddresses([]);
      }
    }
    fetchContracts();
  }, []);

  useEffect(() => {
    if (!isConnected || !address || contractAddresses.length === 0) {
      setBalances([]);
      setNftCount(0);
      return;
    }
    let cancelled = false;
    async function fetchBalances() {
      try {
        const provider = new ethers.providers.Web3Provider(window.ethereum);
        const results = await Promise.all(
          contractAddresses.map(async (contract) => {
            try {
              const nftContract = new ethers.Contract(contract, abi, provider);
              const balance = await nftContract.balanceOf(address);
              return BigNumber.from(balance);
            } catch (e) {
              return BigNumber.from(0);
            }
          })
        );
        if (!cancelled) {
          setBalances(results);
          const total = results.reduce(
            (acc, b) => acc.add(b),
            BigNumber.from(0)
          );
          setNftCount(total.toNumber());
        }
      } catch (e) {
        setBalances([]);
        setNftCount(0);
      }
    }
    fetchBalances();
    return () => {
      cancelled = true;
    };
  }, [contractAddresses, address, isConnected]);

  const {
    data: signMessageData,
    signMessage,
  } = useSignMessage();

  const lastOwnerId = useRef();
  useEffect(() => {
    if (signMessageData) {
      if (address && props.setownerId) {
        if (lastOwnerId.current !== address) {
          props.setownerId(address);
          lastOwnerId.current = address;
        }
      }
      setSignature(signMessageData);
    }
  }, [signMessageData, address]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isConnected) {
      setNftCount(0);
      setSignature(null);
      setIsError(false);
      if (props.setownerId && lastOwnerId.current !== "") {
        props.setownerId("");
        lastOwnerId.current = "";
      }
    }
  }, [isConnected]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSignMessage = async (event) => {
    event?.preventDefault();
    const ownedContracts = contractAddresses.filter(
      (_, idx) => balances[idx] && balances[idx].gt(0)
    );
    const message =
      ownedContracts.length === 1
        ? `I am verifying my ownership of NFTs at ${ownedContracts[0]} with address ${address}`
        : `I am verifying my ownership of NFTs at ${ownedContracts.join(
            " and "
          )} with address ${address}`;
    try {
      if (typeof signMessage === "function") {
        await signMessage({ message });
      } else {
        console.error("signMessage is not a function");
      }
    } catch (error) {
      console.error("Sign Message Error:", error);
      setIsError(true);
    }
  };

  return (
    <NftRoot>
      <NftPanel>
        <NftHint>
          Connect your wallet to verify Station3 Pass ownership.
        </NftHint>

        <ConnectButton.Custom>
          {({
            account,
            chain,
            openAccountModal,
            openChainModal,
            openConnectModal,
            authenticationStatus,
            mounted,
          }) => {
            const ready = mounted && authenticationStatus !== "loading";
            const connected =
              ready &&
              account &&
              chain &&
              (!authenticationStatus ||
                authenticationStatus === "authenticated");

            if (!ready) {
              return (
                <NftPrimaryBtn type="button" disabled>
                  <WalletIcon />
                  Connect Wallet
                </NftPrimaryBtn>
              );
            }

            if (!connected) {
              return (
                <NftPrimaryBtn type="button" onClick={openConnectModal}>
                  <WalletIcon />
                  Connect Wallet
                </NftPrimaryBtn>
              );
            }

            if (chain.unsupported) {
              return (
                <NftWrongNetworkBtn type="button" onClick={openChainModal}>
                  Wrong network
                </NftWrongNetworkBtn>
              );
            }

            return (
              <NftAccountRow>
                <NftChipBtn type="button" onClick={openChainModal}>
                  {chain.hasIcon && chain.iconUrl ? (
                    <img
                      alt=""
                      src={chain.iconUrl}
                      width={14}
                      height={14}
                      style={{ borderRadius: 9999 }}
                    />
                  ) : null}
                  {chain.name ?? "Chain"}
                </NftChipBtn>
                <NftChipBtn type="button" onClick={openAccountModal}>
                  {account.displayName}
                  {account.displayBalance
                    ? ` · ${account.displayBalance}`
                    : ""}
                </NftChipBtn>
              </NftAccountRow>
            );
          }}
        </ConnectButton.Custom>

        {isConnected && address ? (
          <>
            <NftMeta>
              <NftMetaLabel>Wallet address</NftMetaLabel>
              <NftMetaValue title={address}>{address}</NftMetaValue>
            </NftMeta>

            {nftCount > 0 ? (
              <>
                <NftStatusOk>
                  <CheckIcon />
                  You own {nftCount} Station3 Pass
                  {nftCount === 1 ? "" : "es"}
                </NftStatusOk>
                {signature ? (
                  <NftVerified>
                    <CheckIcon />
                    Ownership verified successfully
                  </NftVerified>
                ) : (
                  <NftPrimaryBtn type="button" onClick={handleSignMessage}>
                    Verify Ownership
                  </NftPrimaryBtn>
                )}
              </>
            ) : (
              <NftStatusError>
                <AlertIcon />
                You do not own any Station3 Passes.
                {isError
                  ? " Verification failed — try again after reconnecting."
                  : ""}
              </NftStatusError>
            )}
          </>
        ) : null}
      </NftPanel>
    </NftRoot>
  );
};

export default NFTAuth;
